import { SyntheticEvent, useEffect, useRef } from 'react';

import useAudioRoutingStore from 'state/audioRoutingStore';
import { useAudioVisualizerStore } from 'state/audioVisualizerStore';
import useClientSideRouting, {
  CLIENT_SIDE_PATH,
} from 'state/clientSideRoutingStore';
import { useHeaderStore } from 'state/headerStore';
import { useAudioStore } from 'state/sfx/audioStore';
import { useVoiceAssistantStore } from 'state/voiceAssistantStore';
import useVoTextAnimation from 'state/voTextAnimation';

import S from './AudioRouting.module.scss';

import { useRedirectPreservingParams } from 'hooks/useRedirectPreservingParams';
import useSmoothAudioUpdate from 'hooks/useSmoothAudioUpdate';

export interface AudioRoutingProps {}

interface RedirectParams {
  path?: CLIENT_SIDE_PATH;
  nextPath?: CLIENT_SIDE_PATH;
  shouldRedirect?: boolean;
}

const AudioRouting = () => {
  const isAllowedAudio = useAudioStore(s => s.isAllowed);
  const router = useRedirectPreservingParams();
  const clientRouter = useClientSideRouting();

  const {
    audioChunks,
    isPlaying,
    setIsPlaying,
    setCurrentStep,
    currentStep,
    setCurrentAudioDuration,
  } = useAudioRoutingStore();

  // FIX: subscribe only to the action
  const setTime = useVoTextAnimation(state => state.setTime);

  const nextStep = currentStep + 1;
  const isSideMenuOpen = useHeaderStore(s => s.isSideMenuOpen);

  const audioRefs = useRef<(HTMLAudioElement | null)[]>([]);

  const activeAudioRef = useSmoothAudioUpdate(setTime);

  const mutableActiveAudioRef =
    activeAudioRef as React.MutableRefObject<HTMLAudioElement | null>;

  useEffect(() => {
    setTime(0);
  }, [currentStep, setTime]);

  useEffect(() => {
    const activeAudio = mutableActiveAudioRef.current;

    if (!audioChunks?.length || !activeAudio) return;

    if (isPlaying && !isSideMenuOpen && !activeAudio.ended) {
      activeAudio.play();
    } else {
      activeAudio.pause();
    }
  }, [
    isPlaying,
    isSideMenuOpen,
    audioChunks,
    currentStep,
    mutableActiveAudioRef,
  ]);

  const handleRedirect = ({ path, nextPath }: RedirectParams) => {
    if (!audioChunks) return;

    if (nextStep > audioChunks.length) {
      setIsPlaying(false);
    } else {
      handlePlay();
    }

    sessionStorage.setItem('nextRoute', nextPath as CLIENT_SIDE_PATH);
    clientRouter.setPath(path as CLIENT_SIDE_PATH);
    setCurrentStep(nextStep);
  };

  const handlePlay = () => {
    const activeAudio = mutableActiveAudioRef.current;

    if (activeAudio?.duration) {
      setCurrentAudioDuration(activeAudio.duration);
    }

    setIsPlaying(true);
  };

  const handlePause = (e: SyntheticEvent<HTMLAudioElement, Event>) => {
    const targetAudio = e.target as HTMLAudioElement;

    if (targetAudio.currentTime !== targetAudio.duration) {
      setIsPlaying(false);
    }
  };

  const handleEnded = () => {
    if (!audioChunks?.length) return;

    if (nextStep < audioChunks.length && audioChunks[nextStep]?.path) {
      handleRedirect({
        path: audioChunks[nextStep].path as CLIENT_SIDE_PATH,
        nextPath: audioChunks[nextStep + 1]?.path as CLIENT_SIDE_PATH,
      });
    } else if (nextStep === audioChunks.length) {
      useVoiceAssistantStore.getState().setVoiceAssistantSettings({
        isVoiceAssistantVisible: false,
      });

      useAudioVisualizerStore.getState().setVisible(false);

      setTimeout(() => {
        router.push('/thank-you');
      }, 800);
    }
  };

  const SCRIPT_CHUNKS_MAX_LENGTH = 15;

  return (
    <>
      {[...Array(SCRIPT_CHUNKS_MAX_LENGTH)].map((_, index) => (
        <audio
          key={index}
          ref={el => {
            audioRefs.current[index] = el;

            if (index === currentStep) {
              mutableActiveAudioRef.current = el;
            }
          }}
          autoPlay={index === currentStep && currentStep > 0}
          muted={typeof isAllowedAudio === 'boolean' ? !isAllowedAudio : true}
          onPlay={index === currentStep ? handlePlay : undefined}
          onPause={index === currentStep ? handlePause : undefined}
          onEnded={index === currentStep ? handleEnded : undefined}
          className={S.Audio}
          id={`audioRouting-${index}`}
          preload="auto"
        >
          {audioChunks?.[index]?.audioUrl && (
            <source src={audioChunks[index].audioUrl} type="audio/mpeg" />
          )}
        </audio>
      ))}
    </>
  );
};

export default AudioRouting;
